import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { Tooltip } from "react-tooltip";
import InfoTooltip from "@/components/UI/InfoTooltip";
import { setNumericSetting } from "@/store/tradeSettingsStoreACTIONS";
import {
  EXPIRY_REJECTION_MESSAGES,
  ExpiryRejectionReason,
  formatExpirySeconds,
  validateExpiryDuration,
} from "../../helper/tradeSettingsHelper/expiryDurationHelper";

const REJECTION_MESSAGE_DURATION_MS = 2000;

export const EXPIRY_TIME_DISABLED_TOOLTIP =
  "Time cannot be adjusted when expiry is set for 'GTC' or 'Day'";

// LQC-2170: multi-format duration input for Time to Expiry ("2d 6h 45m").
// Commits total seconds to expiryTimeValue on blur/Enter, clamped to
// [30s, 7d]; invalid input reverts to the last stored value. Disabled
// (expiry type GTC/Day) shows an NA placeholder instead of a value.
export function ExpiryDurationInput({
  esid,
  valueSeconds,
  disabled,
  fontSizeClass,
  idSuffix = "",
  onCommit,
  onRejectionChange,
  rejectionPlacement = "right",
}: {
  esid: string;
  valueSeconds: number | undefined;
  disabled?: boolean;
  fontSizeClass: string;
  // Keeps the DOM ids unique when a second copy renders for the same trade
  // (LQC-2172's modal alongside the settings panel).
  idSuffix?: string;
  // Take the validated seconds instead of writing to tradeSettingsStore - for
  // callers holding their own draft state (LQC-2172's edit-expiry modal).
  onCommit?: (seconds: number) => void;
  // Whether a typed value is currently rejected. Lets a caller with its own
  // submit button ignore the click that caused the rejection (LQC-2366).
  onRejectionChange?: (rejected: boolean) => void;
  // "below" for narrow containers - to the right the message runs outside the
  // edit-expiry popover (LQC-2366). The caller reserves the space.
  rejectionPlacement?: "right" | "below";
}) {
  const [localValue, setLocalValue] = useState<string>(
    formatExpirySeconds(valueSeconds),
  );

  // Rejection message shown beside the input. The id makes each rejection a
  // distinct object so repeating the same mistake restarts the 2s timer.
  const [rejection, setRejection] = useState<{
    reason: ExpiryRejectionReason;
    id: number;
  } | null>(null);
  const rejectionIdRef = useRef(0);

  useEffect(() => {
    setLocalValue(formatExpirySeconds(valueSeconds));
  }, [valueSeconds]);

  useEffect(() => {
    if (!rejection) return;
    const timer = setTimeout(
      () => setRejection(null),
      REJECTION_MESSAGE_DURATION_MS,
    );
    return () => clearTimeout(timer);
  }, [rejection]);

  const handleBlur = () => {
    // Clearing the field is not a rejection - just restore what's stored.
    if (localValue.trim() === "") {
      setLocalValue(formatExpirySeconds(valueSeconds));
      return;
    }
    const result = validateExpiryDuration(localValue);
    if (result.reason) {
      rejectionIdRef.current += 1;
      setRejection({ reason: result.reason, id: rejectionIdRef.current });
      setLocalValue(formatExpirySeconds(valueSeconds));
      // Synchronous, not via an effect: the caller's submit button reads this
      // during the same click that triggered the blur.
      onRejectionChange?.(true);
      return;
    }
    setRejection(null);
    onRejectionChange?.(false);
    setLocalValue(formatExpirySeconds(result.seconds));
    if (onCommit) {
      onCommit(result.seconds);
      return;
    }
    setNumericSetting({
      esid,
      settingField: "expiryTimeValue",
      value: result.seconds,
    });
  };

  // The tooltip id sits on the wrapper so it still fires while the input is
  // disabled (disabled inputs emit no pointer events).
  const disabledTooltipId = `expiryDurationDisabled-${esid}${idSuffix}`;

  return (
    <div className="relative flex w-full items-center">
      <div
        data-tooltip-id={disabled ? disabledTooltipId : undefined}
        className={clsx(
          "flex h-[28px] w-full items-center rounded-md border dark:bg-gray-800",
          rejection
            ? "border-red-600 dark:border-red-500"
            : "border-gray-300 dark:border-gray-600",
          disabled && "cursor-not-allowed opacity-50",
        )}
      >
        <input
          type="text"
          data-testid={`ExpiryDurationInput-${esid}${idSuffix}`}
          id={`ExpiryDurationInput-${esid}${idSuffix}`}
          disabled={disabled}
          placeholder={disabled ? "NA" : "2d 6h 45m"}
          className={clsx(
            "h-full w-full bg-transparent px-2 text-center outline-none dark:text-gray-200",
            disabled && "cursor-not-allowed",
            fontSizeClass,
          )}
          value={disabled ? "" : localValue}
          onChange={(e) => {
            // Only the duration alphabet (digits, d/h/m/s, spaces)
            if (/^[0-9dhms\s]*$/i.test(e.target.value)) {
              setLocalValue(e.target.value);
              onRejectionChange?.(false); // editing again clears the block
            }
          }}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
        />
      </div>
      {disabled && (
        <Tooltip
          id={disabledTooltipId}
          place="top"
          delayShow={100}
          className="z-50 !text-xs"
        >
          {EXPIRY_TIME_DISABLED_TOOLTIP}
        </Tooltip>
      )}
      <InfoTooltip
        id={`expiryDurationInfo-${esid}${idSuffix}`}
        message={
          <div className="flex flex-col gap-1">
            <div>Use the format: 2d 6h 45m</div>
            <ul className="list-disc pl-4">
              <li>d = days</li>
              <li>h = hours</li>
              <li>m = minutes</li>
              <li>s = seconds</li>
            </ul>
            <div>Max: 7d</div>
            <div>Min: 30s</div>
          </div>
        }
      />
      {/* Absolute so the 2s message never reflows the settings columns. */}
      {rejection && (
        <span
          data-testid={`ExpiryDurationRejection-${esid}${idSuffix}`}
          id={`ExpiryDurationRejection-${esid}${idSuffix}`}
          className={clsx(
            "pointer-events-none absolute whitespace-nowrap text-xs text-red-600 dark:text-red-400",
            rejectionPlacement === "below"
              ? "left-0 top-full mt-1"
              : "left-full top-1/2 ml-2 -translate-y-1/2",
          )}
        >
          {EXPIRY_REJECTION_MESSAGES[rejection.reason]}
        </span>
      )}
    </div>
  );
}

export default ExpiryDurationInput;
